import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { usePrefs } from '@/app/prefs-store.ts';
import { Compass, House, Plus, SuitcaseRolling, User } from '@phosphor-icons/react';
import { useT } from '@mosaferati/i18n';
import { TabBar, TabRail, springs, useGlobalScrollState, useIsWide } from '@mosaferati/ui';
import { ensureMe, useMe } from '@/features/auth/useMe.ts';
import { useMeStream } from '@/features/tracking/hooks.ts';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    if (location.pathname !== '/' || usePrefs.getState().onboarded) return;
    // First-ever desktop visit: the marketing site is the entry point. Visitors arriving *from*
    // the site (referrer match) fall through so its "open the app" links still reach the PWA.
    const site = import.meta.env.VITE_SITE_URL;
    if (
      site &&
      typeof matchMedia !== 'undefined' &&
      matchMedia('(min-width: 1024px)').matches &&
      !document.referrer.startsWith(site) &&
      !(await ensureMe())
    )
      throw redirect({ href: site });
    throw redirect({ to: '/onboarding' });
  },
  component: AppLayout,
});

const TABS = [
  { key: 'today', to: '/' as const, icon: House, label: 'nav.today' as const },
  { key: 'trips', to: '/trips' as const, icon: SuitcaseRolling, label: 'nav.trips' as const },
  { key: 'explore', to: '/explore' as const, icon: Compass, label: 'nav.explore' as const },
  { key: 'profile', to: '/profile' as const, icon: User, label: 'nav.profile' as const },
];

function AppLayout() {
  const t = useT();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { me } = useMe();
  // one user-wide stream keeps every ticket card fresh; the segment screen opens its own detailed stream
  useMeStream(!!me && !/^\/segments\//.test(pathname));
  const active = TABS.find((x) => (x.to === '/' ? pathname === '/' : pathname.startsWith(x.to)))?.key ?? 'today';
  // Full-screen flows hide the tab bar on phones (map/sheet first); on wide screens there is room, so keep it reachable.
  const isWide = useIsWide();
  const scroll = useGlobalScrollState();
  const hideTabBar = !isWide && /^\/(segments\/|add-ticket|tickets|plan|search|places\/|notifications|profile\/.|trips\/.)/.test(pathname);
  return (
    <>
      <motion.div key={pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.default} className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </motion.div>
      {/* portaled to <body>: the fixed app shell is its own stacking context, so the bar must live beside (not inside) it to sit above persistent map sheets */}
      {createPortal(
        isWide ? (
          <TabRail
            items={TABS.map((x) => ({ key: x.key, label: t(x.label), icon: x.icon }))}
            activeKey={active}
            onSelect={(k) => {
              const tab = TABS.find((x) => x.key === k)!;
              void navigate({ to: tab.to });
            }}
            action={{ label: t('nav.add'), icon: Plus, onPress: () => void navigate({ to: '/add-ticket' }) }}
          />
        ) : (
          <TabBar
            items={TABS.map((x) => ({ key: x.key, label: t(x.label), icon: x.icon }))}
            activeKey={active}
            onSelect={(k) => {
              const tab = TABS.find((x) => x.key === k)!;
              void navigate({ to: tab.to });
            }}
            action={active !== 'today' ? { label: t('nav.add'), icon: Plus, onPress: () => void navigate({ to: '/add-ticket' }) } : undefined}
            hidden={hideTabBar}
            minimized={scroll.scrolled && scroll.direction === 'down'}
          />
        ),
        document.body,
      )}
    </>
  );
}
